import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { ecaModules, ModuleFeature } from "../ecaModulesData";
import { ecaFeatureIconMap } from "../ecaModuleIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => {
  const Icon = ecaFeatureIconMap[feature.icon];

  return (
    <div className="hms-module-feature eca-module-feature">
      <div className="eca-module-feature__head">
        <span className="eca-module-feature__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="hms-module-feature__title eca-module-feature__title">{feature.title}</h4>
      </div>
      {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
      {feature.items && (
        <ul className="hms-module-feature__list">
          {feature.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EcaModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(ecaModules[0].id);
  const activeModule = ecaModules.find((m) => m.id === activeId) ?? ecaModules[0];

  const tabLabel = (title: string) =>
    title.replace(" Features", "").replace(" Panel", "").replace(" Dashboard", "");

  return (
    <section className="hms-modules" aria-labelledby="eca-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="eca-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            App Features
          </h2>
          <p className="hms-modules__lead">
            Separate feature sets for the customer app, vendor panel, and admin dashboard —
            built for single stores and multi-vendor marketplaces.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs" role="tablist" aria-label="E-commerce app modules">
          {ecaModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`eca-panel-${mod.id}`}
              id={`eca-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {tabLabel(mod.title)}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`eca-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`eca-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default EcaModulesSection;
